// request-note-text.ts — CTSK-TBD (child of CTSK-166).
//
// The "Request note" prompt an operator copies from the External Clients
// → Onboarding card and pastes into a fresh tasko-mcp session to spin up a
// new access request. Kept out of the JSX so the string is testable in
// isolation and future wording tweaks don't touch the component.
//
// Example values are the CTSK-166 docs' canonical sample
// (docs/tasko-mcp-setup.md §"The full pipeline": `bf9bde2f` /
// `clipper-ingest`) — deliberately filled-in, not placeholders, so the
// operator sees what a complete call looks like. No secrets live here;
// request-note-text.test.ts guards against one sneaking in.

export const REQUEST_NOTE_PROMPT: string = `# Captain Tasko — new external-client access request

From this session (which has tasko-mcp configured with a bootstrap PAT):

1. Confirm you are online with Captain Tasko:
   Call tasko_whoami. Report orgId, capabilities, and
   session.sub_uuid_tail_first8 (if present).

2. Submit the access request. Fill in your own values, then call:

   tasko_request_access({
     subUuidTail: "bf9bde2f",
       # 8-char tail from your sessionMetadata.subUuid.
       # tasko-mcp uses this to pick the right PAT slot in your
       # mcp-config file. It is NEVER sent to the server.
       # Discover yours: run tasko_load_session_env on boot and
       # read sessionMetadata.subUuid from the returned env block.

     sessionMetadata: {
       subUuid: "local_bf9bde2f-cf51-51e2-bd32-ccfb48725a0a"
         # Full session UUID the server derives
         # sub_uuid_tail_first8 from.
         #   local_<hex-uuid>   — desktop sessions (lowercased)
         #   cse_<24-char>      — cloud sessions (case-sensitive)
     },

     role: "clipper-ingest",
       # A slug from docs/role-catalog.md.
       # Pick the smallest role that covers what you will do.

     rationale: "Clipper staging ingest, CTSK-515 bundle imports, through 2026-11-30"
       # One line: who, what, why, duration.
   })

3. Report back:
   The returned request id (externalClientRequest.id) and the row's
   sourceTag / friendlyName so I can locate it on
   External Clients -> Pending in Mission Control.

   Do NOT paste mint token values here. Approval and hand-off happen
   out-of-band via the secure channel we already agreed on.

If tasko-mcp is not reachable in this session, stop and tell me — the
direct POST /api/v1/documents/ingest fallback requires my admin PAT,
not yours. See the Ingest API contract link on the admin page.
`;

/** Lines of the prompt the chip's hover preview shows. Edit here, not via prop. */
export const REQUEST_NOTE_PREVIEW_LINES: number = 8;

/** One-sentence usage note rendered under the preview in the tooltip. */
export const REQUEST_NOTE_USAGE =
  'Copy, then paste into a fresh tasko-mcp session; the request lands on this page as a pending row.';

/**
 * First `lines` lines of `text`, with a trailing `…` when anything was
 * cut. Returns the whole input untouched when it fits.
 */
export function previewOf(text: string, lines: number): string {
  const all = text.split('\n');
  if (lines <= 0) return '…';
  if (all.length <= lines) return text;
  return all.slice(0, lines).join('\n') + '\n…';
}
